import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserTie,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaEye,
  FaBuilding,
  FaBriefcase,
  FaIdCard,
} from "react-icons/fa";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";
import { useNavigate } from "react-router-dom";
import EmployeeDetails from "../../Components/details/EmployeeDetails";

export default function EmployeeList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  const handleViewDetails = async (id) => {
    setSelectedEmployeeId(id);
    setShowModal(true);
    setDetailsLoading(true);
    try {
      const res = await axios.get(`${server}/employees/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployeeDetails(res.data.data);
    } catch (error) {
      toast.error("Failed to load details");
      setShowModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/employees`, {
        params: { search },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItems(res.data.data || []);
    } catch (error) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => {
      fetchItems();
    }, 400);
    return () => clearTimeout(timer);
  }, [search, token]);

  const handleDelete = async (id) => {
    if (window.confirm("Delete employee record?")) {
      try {
        await axios.delete(`${server}/employees/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Employee deleted successfully");
        fetchItems();
      } catch (error) {
        toast.error("Error deleting employee");
      }
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaUserTie size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Employee & Staff Directory</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Manage teaching faculty, administrative staff, and employment details</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/my/employees/id-card")}
            className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5 border border-slate-200 dark:border-slate-700"
          >
            <FaIdCard size={11} /> Generate ID Cards
          </button>
          <button
            onClick={() => navigate("/my/employees/register")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaPlus size={10} /> Register Employee
          </button>
        </div>
      </div>

      {/* Quick KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Staff Members</span>
          <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">{items.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Active Employment</span>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
            {items.filter((e) => e.isActive !== false).length}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Search Query</span>
          <div className="text-xs font-bold text-gray-700 dark:text-slate-300 mt-1 truncate">
            {search || "Showing All"}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Academic Session</span>
          <div className="text-sm font-black text-gray-900 dark:text-white mt-0.5">2026 - 2027</div>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white outline-none"
            placeholder="Search staff by Name or Employee Code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Compact High-Density Employee Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800/80 text-gray-500 dark:text-slate-400 font-black text-[10px] uppercase border-b border-gray-200 dark:border-slate-800">
                <th className="p-2.5">Staff Profile</th>
                <th className="p-2.5">Staff Code</th>
                <th className="p-2.5">Department</th>
                <th className="p-2.5">Designation & Contact</th>
                <th className="p-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-indigo-600 font-bold">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    Loading staff directory...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-400 italic">
                    No matching employee records found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                    <td className="p-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-black text-xs flex items-center justify-center border border-indigo-200 dark:border-indigo-800 overflow-hidden shrink-0">
                          {item.profilePictureUrl ? (
                            <img src={item.profilePictureUrl} alt={item.name} className="h-full w-full object-cover" />
                          ) : (
                            item.name?.[0] || "E"
                          )}
                        </div>
                        <div>
                          <div className="font-black text-gray-900 dark:text-white text-xs">{item.name || "Unknown"}</div>
                          <div className="text-[10px] text-gray-400 font-medium">{item.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2.5 font-mono font-bold text-indigo-600 dark:text-indigo-400">{item.employeeCode}</td>
                    <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                      {item.department?.name || item.departmentName || "N/A"}
                    </td>
                    <td className="p-2.5 font-medium text-gray-700 dark:text-slate-300">
                      <div className="flex items-center gap-2">
                        {item.designation && (
                          <span className="px-2 py-0.5 bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 rounded-md font-bold text-[10px]">
                            {item.designation}
                          </span>
                        )}
                        <span className="text-gray-500">{item.mobileNo || ""}</span>
                      </div>
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => handleViewDetails(item.id)}
                          className="p-1.5 text-emerald-600 bg-emerald-50 dark:bg-emerald-950 rounded-lg hover:bg-emerald-100 transition"
                          title="View Details"
                        >
                          <FaEye size={11} />
                        </button>
                        <button
                          onClick={() => navigate(`/my/employees/edit/${item.id}`)}
                          className="p-1.5 text-indigo-600 bg-indigo-50 dark:bg-indigo-950 rounded-lg hover:bg-indigo-100 transition"
                          title="Edit Employee"
                        >
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-1.5 text-rose-600 bg-rose-50 dark:bg-rose-950 rounded-lg hover:bg-rose-100 transition"
                          title="Delete Employee"
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/80">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaUserTie className="text-indigo-500" /> Staff Profile Details
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEmployeeDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-xs font-bold px-2 py-1 rounded-lg"
              >
                ✕ Close
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 custom-scrollbar text-xs">
              {detailsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-indigo-600 font-bold">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mb-2"></div>
                  Loading staff details...
                </div>
              ) : employeeDetails ? (
                <EmployeeDetails
                  employee={employeeDetails}
                  onClose={() => {
                    setShowModal(false);
                    setEmployeeDetails(null);
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
